import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import AuthService from "../AuthService/AuthService";
import "./Login.css";

const IS_DESKTOP = process.env.REACT_APP_ENV === "desktop";

const Login = () => {
  const [formData, setFormData] = useState({
    phone_number: "",
    password: "",
  });

  const [showPassword, setShowPassword]     = useState(false);
  const [error, setError]                   = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading]               = useState(false);
  const [authMode, setAuthMode]             = useState("cloud");
  const navigate = useNavigate();

  useEffect(() => {
    const env = process.env.REACT_APP_ENV;
    if (env === "desktop") {
      setAuthMode("desktop");
    } else {
      setAuthMode("cloud");
    }
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setLoading(true);

    try {
      let loginUrl;

      if (authMode === "desktop") {
        const desktopBaseUrl = process.env.REACT_APP_DESKTOP_APP_API_BASE_URL;
        if (!desktopBaseUrl) {
          throw new Error(
            "Desktop backend URL not configured. Check REACT_APP_DESKTOP_APP_API_BASE_URL"
          );
        }
        loginUrl = `${desktopBaseUrl}/auth/login/pharmacy/`;
      } else {
        const cloudBaseUrl = process.env.REACT_APP_API_BASE_URL;
        if (!cloudBaseUrl) {
          throw new Error(
            "Cloud backend URL not configured. Check REACT_APP_API_BASE_URL"
          );
        }
        loginUrl = `${cloudBaseUrl}/auth/login/pharmacy/`;
      }

      const response = await fetch(loginUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        // ── Store all tokens BEFORE navigating ──
        AuthService.setTokens(data.access_token, data.refresh_token);

        localStorage.setItem("user_id",       data.user_id);
        localStorage.setItem("pharmacy_id",   data.pharmacy_id || "");
        localStorage.setItem("branch_id",     data.branch_id   || "");
        localStorage.setItem("staff_id",      data.staff_id    || "");
        localStorage.setItem("user_type",     data.user_type   || "");
        localStorage.setItem("auth_mode",     authMode);

        // ── Always stay logged in — desktop requires persistent session
        // for background extension sync to work without interruption ──
        localStorage.setItem("stay_logged_in", "true");

        if (data.user_data) {
          localStorage.setItem("user_data", JSON.stringify(data.user_data));
        }

        // ── Store cloud tokens for cloudApi (desktop only) ──
        if (IS_DESKTOP && data.cloud_access_token) {
          localStorage.setItem("cloud_access_token",  data.cloud_access_token);
          localStorage.setItem("cloud_refresh_token", data.cloud_refresh_token || "");
        }

        setSuccessMessage(data.message || "Login successful!");
        window.dispatchEvent(new Event("authChange"));
        setTimeout(() => navigate("/dashboard"), 1000);

      } else {
        if (response.status === 403) {
          setError(data?.detail || "Invalid phone number or password.");
        } else if (response.status === 503) {
          setError(
            data?.detail ||
              "Service unavailable. Please try again or check your connection."
          );
        } else if (response.status === 400) {
          setError(data?.detail || "Invalid request. Please check your input.");
        } else {
          setError(
            data?.detail ||
              data?.message ||
              `Login failed (${response.status}). Please try again.`
          );
        }
      }
    } catch (err) {
      if (
        IS_DESKTOP &&
        (err instanceof TypeError ||
          err.message?.toLowerCase().includes("failed to fetch") ||
          err.message?.toLowerCase().includes("econnrefused") ||
          err.message?.toLowerCase().includes("network") ||
          err.message?.toLowerCase().includes("load failed"))
      ) {
        setError(
          "The app is still starting up. Please wait a few seconds and try again."
        );
      } else if (err.message.includes("not configured")) {
        setError(`Configuration error: ${err.message}`);
      } else if (err instanceof TypeError) {
        setError("Network error. Check your connection and backend status.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="crypto-signup-container">
      <form className="crypto-signup-form" onSubmit={handleSubmit}>
        <h2>🏥 Pharmacy Login</h2>

        {authMode === "desktop" && (
          <div style={{
            background: "#e8f5e9", border: "1px solid #4caf50", borderRadius: "6px",
            padding: "0.75rem", marginBottom: "1rem", textAlign: "center",
            fontSize: "0.9rem", color: "#2e7d32", fontWeight: "500",
          }}>
            🖥️ Desktop Mode (Local-First with Cloud Sync)
          </div>
        )}

        {authMode === "cloud" && (
          <div style={{
            background: "#e3f2fd", border: "1px solid #2196f3", borderRadius: "6px",
            padding: "0.75rem", marginBottom: "1rem", textAlign: "center",
            fontSize: "0.9rem", color: "#1565c0", fontWeight: "500",
          }}>
            ☁️ Cloud Mode
          </div>
        )}

        {error && (
          <div className="crypto-error">
            {error}
            {IS_DESKTOP && error.includes("still starting up") && (
              <p style={{ margin: "0.5rem 0 0", fontSize: "0.82rem", opacity: 0.85 }}>
                The backend usually takes 10–15 seconds on first launch.
              </p>
            )}
          </div>
        )}

        {successMessage && (
          <div className="crypto-success">{successMessage}</div>
        )}

        <input
          type="text"
          name="phone_number"
          placeholder="Phone Number"
          value={formData.phone_number}
          onChange={handleChange}
          required
        />

        <div className="password-wrapper">
          <input
            type={showPassword ? "text" : "password"}
            name="password"
            placeholder="Password"
            value={formData.password}
            onChange={handleChange}
            required
          />
          <span
            className="password-toggle-icon"
            onClick={togglePasswordVisibility}
            style={{ cursor: loading ? "not-allowed" : "pointer" }}
          >
            {showPassword ? <FaEyeSlash /> : <FaEye />}
          </span>
        </div>

        <div className="forgot-password">
          <Link to="/PasswordResetRequest">Forgot Password?</Link>
        </div>

        <button type="submit" disabled={loading}>
          {loading ? "🔄 Logging in..." : "Login"}
        </button>

        <p>
          Don't have a pharmacy account? <Link to="/SignUp">Register</Link>
        </p>

        <div style={{
          marginTop: "1.5rem", paddingTop: "1.5rem",
          borderTop: "1px solid #eee", fontSize: "0.85rem",
          color: "#666", textAlign: "center",
        }}>
          <p style={{ margin: "0.5rem 0" }}>🔐 Credentials encrypted & secure</p>
        </div>
      </form>
    </div>
  );
};

export default Login;